import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px } from '../theme';
import { createButton } from '../ui/button';
import { showAdviser } from '../ui/adviserPanel';
import { portraitKey, addBackdrop, MENU_BACKDROP } from '../assets';
import { drawFallbackPortrait } from '../render/portrait';
import type { Campaign } from '@core/campaign/campaign';
import { audio } from '../audio/engine';
import { DRONE_ROOT } from '../audio/motifs';

/**
 * Der Einstieg in einen neuen Durchlauf.
 *
 * Zwischen Editor und Station steht ein Bildschirm, der die Figur einmal
 * ausschreibt: Gesicht, Name, Herkunft und der Verlust, der sie hierher
 * gebracht hat. Bis M6 begann das Spiel damit, dass man ohne Vorgeschichte an
 * einer Station stand und eine Liste sah — die Figur war Statistik.
 *
 * Danach begruesst Dr. Roskopf. Sie hat zu jeder Herkunft etwas anderes zu
 * sagen, und sie sagt es knapp: Wer hier ankommt, hat schon einmal jemanden
 * verloren, und niemand im Institut haelt dazu eine Rede.
 */
export class IntroScene extends Phaser.Scene {
  private campaign!: Campaign;

  constructor() {
    super('Intro');
  }

  create(): void {
    this.campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    const { width, height } = this.scale;
    const centreX = width / 2;
    const character = this.campaign.character;

    // Das Wrack aus dem Menue, noch weiter abgedunkelt.
    addBackdrop(this, MENU_BACKDROP, 0.3);

    const portraitWidth = 176;
    const portraitHeight = 220;
    const columnGap = 40;
    const textLeft = centreX - (portraitWidth + columnGap) / 2 + portraitWidth + columnGap;
    const portraitLeft = centreX - (portraitWidth + columnGap) / 2;
    const textWidth = Math.min(360, width - textLeft - 40);

    this.add
      .image(portraitLeft, 108, this.portraitTexture(character.portraitId))
      .setOrigin(0, 0)
      .setDisplaySize(portraitWidth, portraitHeight);

    this.add.text(centreX, 52, t('intro.title'), titleStyle(22)).setOrigin(0.5);

    this.add.text(textLeft, 112, character.name, {
      fontFamily: FONT.display,
      fontSize: px(26),
      color: PALETTE.ink,
    });

    this.add.text(textLeft, 148, t(`background.${character.background}`), {
      fontFamily: FONT.mono,
      fontSize: px(12),
      color: TEXT_COLOUR.base,
    });

    // Die Herkunft in eigenen Worten — je Hintergrund eine eigene Fassung,
    // in der dritten Person, damit sie wie eine Akte klingt und nicht wie ein
    // Tagebuch.
    const blurb = this.add.text(textLeft, 176, t(`intro.${character.background}`), {
      ...readoutStyle(12),
      wordWrap: { width: textWidth },
      lineSpacing: 4,
    });

    // Der Verlust setzt unter dem Absatz auf, nicht auf einer festen Zeile:
    // Die drei Herkunftstexte sind verschieden lang, und in der laengsten
    // Fassung stiessen die beiden sonst aneinander.
    const dividerY = blurb.y + blurb.height + 18;
    this.add.rectangle(textLeft, dividerY, 48, 1, PALETTE.panelEdge).setOrigin(0, 0);

    this.add.text(
      textLeft,
      dividerY + 16,
      t('intro.lost', {
        name: character.lostName,
        relation: t(character.lostRelationKey),
      }),
      {
        ...readoutStyle(12),
        color: PALETTE.inkDim,
        fontStyle: 'italic',
        wordWrap: { width: textWidth },
        lineSpacing: 3,
      },
    );

    createButton(this, centreX, height - 56, t('intro.begin'), () => this.scene.start('WorldMap'), {
      width: 220,
    });

    this.cameras.main.fadeIn(600, 5, 8, 13);

    audio.startDrone(DRONE_ROOT.base);
    audio.setTension(0.2);

    // Roskopf kommt einen Moment spaeter — erst soll man die eigene Figur
    // ansehen koennen, dann meldet sich das Institut.
    this.time.delayedCall(900, () => {
      showAdviser(this, {
        text: t(`adviser.welcome.${character.background}`),
        dismissKey: 'adviser.welcome.ack',
      });
    });
  }

  private portraitTexture(id: string): string {
    const key = portraitKey(id);
    if (this.textures.exists(key)) return key;

    const fallback = `portrait-fallback:${id}`;
    if (!this.textures.exists(fallback)) {
      this.textures.addCanvas(fallback, drawFallbackPortrait(id, 128));
    }
    return fallback;
  }
}
